// COMPETE AND WIN CASH (the Compete and Win Cash spec): the competition's settings, shared by the
// api, the workers, the park office and the phones. Two games, Keke Grand Prix and City Hustle,
// play only inside one weekly window (all of Saturday, 12am to 11:59pm Lagos time). Every registered profile
// gets a few scored runs per game; the best counts, and the top places win naira prizes paid by
// bank transfer. Entry is free and nothing bought ever changes a score. Every number is here, set
// in the park office, never in code.
import { z } from 'zod';
/** The two games of the category (engine names; the screen says Keke Grand Prix and City Hustle). */
export const COMPETE_GAMES = ['grandprix', 'hustle'];
/** How a game's board is ordered: Grand Prix by time (lower wins), City Hustle by takings (higher wins). */
export const higherWins = (game) => game === 'hustle';
/** The Grand Prix tracks a Saturday may race on (its Track of the Week; each a ring in RACE_TRACKS); the first is the default. */
export const COMPETE_TRACKS = ['third-mainland', 'oshodi-loop', 'lekki-sprint', 'apapa-port-run'];
export const isCompeteGame = (s) => COMPETE_GAMES.includes(s);
/** How much trouble the bots make, set per track and city and shown in the hub (Calm, Lagos, Wahala). */
export const BOT_TROUBLE = ['calm', 'lagos', 'wahala'];
/** Lagos keeps West Africa Time all year: UTC+1, no daylight saving. */
export const WAT_OFFSET_MS = 3_600_000;
const MINUTE = 60_000;
const DAY = 86_400_000;
const minuteOfDay = z
    .number()
    .int()
    .min(0)
    .max(24 * 60);
/** Prize money in whole naira (real money, paid by transfer): never park money, never fare. */
const naira = z.number().int().min(0).max(10_000_000);
/**
 * Bot trouble (the spec's "Bots make trouble, fairly"): how many challenges (a block, a bump, a
 * rider or a pump taken) each real player meets per Grand Prix lap and per City Hustle game hour
 * at each level, the same for everyone; and the most a bot may hold one keke still at once.
 */
export const botTroubleSchema = z.strictObject({
    perLap: z.strictObject({
        calm: z.number().int().min(0).max(10),
        lagos: z.number().int().min(0).max(10),
        wahala: z.number().int().min(0).max(10),
    }),
    perGameHour: z.strictObject({
        calm: z.number().int().min(0).max(10),
        lagos: z.number().int().min(0).max(10),
        wahala: z.number().int().min(0).max(10),
    }),
    holdMaxMs: z.number().int().min(0).max(10_000),
});
const gameSchema = z.strictObject({
    /** On the hub and playable in the window. */
    on: z.boolean(),
    /** The default trouble level; a Saturday may set its own before it opens. */
    botTrouble: z.enum(BOT_TROUBLE),
    /** Seats in one multiplayer heat (bots fill the rest at the start when botFill is on). */
    heatSeats: z.number().int().min(2).max(12),
    /** The prizes by place (1st first), in naira, never rising down the places. */
    prizes: z.array(naira).min(1).max(10),
});
export const competeSchema = z
    .strictObject({
    /** The category on the Play menu. Off in production until its launch gate. */
    on: z.boolean(),
    /** The weekly window, in Lagos time: the day (0 Sunday to 6 Saturday) and minutes after midnight. */
    window: z.strictObject({
        weekday: z.number().int().min(0).max(6),
        opens: minuteOfDay,
        /** The last minute a scored run may start (a run going at the close finishes and counts). */
        lastStart: minuteOfDay,
        closes: minuteOfDay,
        /** Test world only (staging tries runs during the week): the window opens every day. */
        everyDay: z.boolean().optional(),
    }),
    /** Scored runs each profile gets per game, each window. */
    runsPerGame: z.number().int().min(1).max(10),
    /** A heat starts this often from the opening, and its lobby opens this long before. */
    heatEveryMin: z.number().int().min(5).max(60),
    lobbyOpensMin: z.number().int().min(1).max(10),
    /** Bots fill a heat's empty seats at its start. */
    botFill: z.boolean(),
    /** A connection lost this long after Go ends the run with its score so far. */
    dropMs: z.number().int().min(5_000).max(120_000),
    botTrouble: botTroubleSchema,
    games: z.strictObject({ grandprix: gameSchema, hustle: gameSchema }),
})
    .superRefine((c, ctx) => {
    const w = c.window;
    if (!(w.opens < w.lastStart && w.lastStart < w.closes))
        ctx.addIssue({ code: 'custom', path: ['window', 'lastStart'], message: 'Between the opening and the close.' });
    if (w.closes - w.opens < c.heatEveryMin)
        ctx.addIssue({ code: 'custom', path: ['heatEveryMin'], message: 'At least one heat in the window.' });
    for (const g of COMPETE_GAMES) {
        const p = c.games[g].prizes;
        if (p.some((v, i) => i > 0 && v > p[i - 1]))
            ctx.addIssue({ code: 'custom', path: ['games', g, 'prizes'], message: 'A lower place never pays more.' });
    }
});
/** The most one Saturday may pay out over both games (a guard against a typed extra zero). */
export const COMPETE_PRIZE_TOTAL_MAX = 10_000_000;
/** Places a game may pay. */
export const COMPETE_PRIZE_PLACES = 10;
/**
 * A Saturday's prizes as the park office sets them: per game, 1st first, in whole naira. A lower
 * place never pays more; places that pay nothing are dropped from the end; the whole Saturday stays
 * under COMPETE_PRIZE_TOTAL_MAX.
 */
export const competePrizesSchema = z
    .strictObject({
    grandprix: z.array(naira).min(1).max(COMPETE_PRIZE_PLACES),
    hustle: z.array(naira).min(1).max(COMPETE_PRIZE_PLACES),
})
    .superRefine((p, ctx) => {
    for (const g of COMPETE_GAMES) {
        const list = p[g];
        if (list.some((v, i) => i > 0 && v > list[i - 1]))
            ctx.addIssue({ code: 'custom', path: [g], message: 'A lower place never pays more.' });
        if (!((list[0] ?? 0) > 0))
            ctx.addIssue({ code: 'custom', path: [g], message: '1st place pays something.' });
    }
    if (prizeTotal(p.grandprix) + prizeTotal(p.hustle) > COMPETE_PRIZE_TOTAL_MAX)
        ctx.addIssue({ code: 'custom', path: [], message: 'Over the most a Saturday may pay.' });
});
/** What a list of prizes pays in all. */
export const prizeTotal = (list) => list.reduce((a, b) => a + b, 0);
/** Places that pay nothing come off the end. */
export const trimPrizes = (list) => {
    const out = [...list];
    while (out.length > 1 && out[out.length - 1] === 0)
        out.pop();
    return out;
};
/**
 * Saturdays all day since 2026-10-24 (the owner's call; the spec had 7am to 12pm): open from 12am,
 * the last start at 11:50pm, the close at 11:59pm. 3 runs per game, a heat every 15 minutes (its
 * lobby opens 3 minutes before), the proposed prizes. On in every world since the
 * owner opened it in production on 2026-10-24 (the Play menu's cash tile). Each game is switched on
 * or off on the park office's Game types (play.cashGames); City Hustle on since 2026-10-24.
 */
export const DEFAULT_COMPETE = {
    on: true,
    window: { weekday: 6, opens: 0, lastStart: 23 * 60 + 50, closes: 23 * 60 + 59 },
    runsPerGame: 3,
    heatEveryMin: 15,
    lobbyOpensMin: 3,
    botFill: true,
    dropMs: 30_000,
    botTrouble: {
        perLap: { calm: 1, lagos: 2, wahala: 4 },
        perGameHour: { calm: 1, lagos: 2, wahala: 3 },
        holdMaxMs: 3_000,
    },
    games: {
        grandprix: { on: true, botTrouble: 'lagos', heatSeats: 8, prizes: [100_000, 50_000, 25_000] },
        hustle: { on: true, botTrouble: 'lagos', heatSeats: 12, prizes: [100_000, 50_000, 25_000] },
    },
};
/**
 * Staging's window (COMPETE_EVERY_DAY): every day, all day, so the team can test any time: runs
 * until 11:50pm, the close at 11:59pm (its results and payouts written each night).
 */
export const STAGING_WINDOW = {
    weekday: 6,
    opens: 0,
    lastStart: 23 * 60 + 50,
    closes: 23 * 60 + 59,
    everyDay: true,
};
/** Validate competition settings from the park office. Errors are keyed by field path for the form. */
export function parseCompete(input) {
    const r = competeSchema.safeParse(input);
    if (r.success)
        return { ok: true, config: r.data };
    const errors = {};
    for (const issue of r.error.issues)
        errors[issue.path.join('.') || '_'] ??= issue.message;
    return { ok: false, errors };
}
/** The window now open, or else the next one to open (a closed hub counts down to it). */
export function windowAt(now, c) {
    const w = c.window;
    const local = now + WAT_OFFSET_MS;
    const midnight = local - (((local % DAY) + DAY) % DAY);
    const weekday = new Date(midnight).getUTCDay();
    let ahead = w.everyDay ? 0 : (w.weekday - weekday + 7) % 7;
    if (ahead === 0 && local >= midnight + w.closes * MINUTE)
        ahead = w.everyDay ? 1 : 7;
    const start = midnight + ahead * DAY;
    return {
        day: new Date(start).toISOString().slice(0, 10),
        opensAt: start + w.opens * MINUTE - WAT_OFFSET_MS,
        lastStartAt: start + w.lastStart * MINUTE - WAT_OFFSET_MS,
        closesAt: start + w.closes * MINUTE - WAT_OFFSET_MS,
    };
}
/** Is the window open now (the hub plays), and may a scored run start now? */
export function windowState(now, win) {
    const open = now >= win.opensAt && now < win.closesAt;
    return { open, canStart: open && now <= win.lastStartAt };
}
/** Every heat's start in a window: from the opening, every heatEveryMin, none after the last start. */
export function heatsIn(win, c) {
    const every = c.heatEveryMin * MINUTE;
    const out = [];
    for (let t = win.opensAt; t <= win.lastStartAt; t += every)
        out.push(t);
    return out;
}
/** The next heat to start at or after now in this window (null when none is left). */
export function nextHeat(now, win, c) {
    return heatsIn(win, c).find((t) => t >= now) ?? null;
}
// ------------------------------------------------------------ City Hustle (the spec's Competition 2)
/** The kinds of rider (the spec's Passengers table): engine ids, each with its own fare and ways. */
const riderFactors = z.strictObject({
    student: z.number().min(0.1).max(5),
    office: z.number().min(0.1).max(5),
    market: z.number().min(0.1).max(5),
    night: z.number().min(0.1).max(5),
});
const riderTimes = z.strictObject({
    student: z.number().int().min(0).max(20_000),
    office: z.number().int().min(0).max(20_000),
    market: z.number().int().min(0).max(20_000),
    night: z.number().int().min(0).max(20_000),
});
/**
 * City Hustle's balance numbers (the simulation that plays them is @wahala/simulation/hustle). Money
 * is in the game's naira (the takings are a score, never paid out or turned into park money); times
 * are ms of play; distances are tiles; speeds are factors on the Park keke's.
 */
export const hustleSchema = z
    .strictObject({
    /** One shift, 6am to 10pm on the game clock, played in this long (one game hour is a sixteenth). */
    shiftMs: z.number().int().min(60_000).max(1_800_000),
    /** The danfo lights before Go (the run is spent at Go). */
    countdownMs: z.number().int().min(0).max(10_000),
    /** What the driver starts with: cash in hand and fuel in litres (of a tank). */
    float: z.number().int().min(0).max(100_000),
    tankLitres: z.number().min(1).max(20),
    startLitres: z.number().min(0).max(20),
    /** Litres burnt per tile driven. */
    litresPerTile: z.number().min(0).max(1),
    /** The keke's top speed, and on an expressway (the fastest way across, and banned). */
    speed: z.number().min(0.5).max(2),
    expresswaySpeed: z.number().min(1).max(2),
    /** Price per litre at the cheapest station (dearer stations carry their own share on the map). */
    fuelPrice: z.number().int().min(1).max(10_000),
    /** At a pump: the wait in the queue at busy hours and quiet ones, then so long a litre. */
    queueBusyMs: z.number().int().min(0).max(60_000),
    queueQuietMs: z.number().int().min(0).max(60_000),
    fillMsPerLitre: z.number().int().min(0).max(5_000),
    /** Fares: a base plus so much a tile of the road, by the rider's kind, raised at rush hours and in rain. */
    fareBase: z.number().int().min(0).max(10_000),
    farePerTile: z.number().int().min(0).max(1_000),
    riderFare: riderFactors,
    rushFactor: z.number().min(1).max(3),
    rainFactor: z.number().min(1).max(3),
    /** A drop at the wrong stop pays this share; a quick ride tips this share on top. */
    wrongDropShare: z.number().min(0).max(1),
    tipShare: z.number().min(0).max(1),
    /** Loading and unloading, by kind (the market woman's load is slow). */
    loadMs: riderTimes,
    /** An office rider leaves with nothing after this many game hours aboard; a waiting rider gives up after waitHours. */
    officePatienceHours: z.number().min(0.25).max(8),
    waitHours: z.number().min(0.25).max(8),
    /** How often riders may appear (each stop draws its chance), and a scale on every district's riders. */
    spawnEveryMs: z.number().int().min(250).max(30_000),
    busyScale: z.number().min(0).max(5),
    /** Seats in the back; one more rider than this is overloading. */
    seats: z.number().int().min(1).max(6),
    /** How close a keke stands to take or drop a rider, buy, bank or settle (tiles). */
    reach: z.number().min(0.5).max(4),
    /** The motor park levy each entry, the day ticket that covers every park and agbero, an agbero's settle. */
    parkLevy: z.number().int().min(0).max(10_000),
    dayTicket: z.number().int().min(0).max(20_000),
    settle: z.number().int().min(0).max(10_000),
    /** A latched agbero slows the keke to this until he is settled. */
    latchSlow: z.number().min(0.1).max(1),
    /** Not settled: the agbero takes this share of every fare while he hangs on, and after latchForceMs he seizes twice the settle by force, holding the keke seizeHoldMs. */
    latchCut: z.number().min(0).max(1).default(0.25),
    latchForceMs: z.number().int().min(5_000).max(600_000).default(30_000),
    seizeHoldMs: z.number().int().min(0).max(60_000).default(6_000),
    /** Banking leaves this much cash in hand. */
    bankKeep: z.number().int().min(0).max(100_000),
    /** Police: pay roger, or wait this long while they check papers; the choice waits so long, then they make you wait. */
    roger: z.number().int().min(0).max(10_000),
    policeWaitMs: z.number().int().min(0).max(60_000),
    policeChoiceMs: z.number().int().min(1_000).max(30_000),
    /** How close a checkpoint waves a keke down (tiles), and how long before the same one waves it again. */
    policeReach: z.number().min(0.5).max(4),
    policeCoolMs: z.number().int().min(0).max(600_000),
    /**
     * The city VIO: sees an offence within sightTiles (and a clear line); Bribe or Bail's needle runs
     * needleMs over the lawyer, the sweet spot and wahala; the sweet spot takes bribeShare of cash in hand.
     * After a stop, coolMs before the next.
     */
    vio: z.strictObject({
        sightTiles: z.number().min(1).max(15),
        needleMs: z.number().int().min(1_500).max(10_000),
        lawyer: z.number().min(0.02).max(0.5),
        sweet: z.number().min(0).max(0.9),
        coolMs: z.number().int().min(0).max(120_000),
        vanTilesPerSec: z.number().min(0.5).max(10),
    }),
    bribeShare: z.number().min(0).max(1),
    /** Repairs: crashes dent the keke (each dent slows it; this many smoke and the VIO may stop you). */
    dentSlow: z.number().min(0).max(0.3),
    dentsMax: z.number().int().min(1).max(6),
    /** Hitting a wall, a building or a kerb at this share of top speed or faster is a crash: stopped dead, shaken, a dent. */
    wallCrash: z.number().min(0.2).max(2).default(0.8),
    smokeAt: z.number().int().min(1).max(6),
    dentFix: z.number().int().min(0).max(20_000),
    /** A pothole hit faster than this share of top speed punctures a tyre: limp at punctureSlow to a vulcaniser. */
    potholeSpeed: z.number().min(0).max(1),
    punctureSlow: z.number().min(0.1).max(1),
    puncture: z.number().int().min(0).max(20_000),
    /** Out of fuel: push the keke at this. */
    pushSpeed: z.number().min(0.05).max(1),
    /** A bridge go-slow (a Saturday event) slows the bridges to this. */
    goslowSlow: z.number().min(0.1).max(1),
    /** The keke owner's fixed cut, taken at the shift's end (the same for everyone). */
    ownerDelivery: z.number().int().min(0).max(100_000),
})
    .superRefine((h, ctx) => {
    if (h.startLitres > h.tankLitres)
        ctx.addIssue({ code: 'custom', path: ['startLitres'], message: 'Inside the tank.' });
    if (h.vio.lawyer + h.vio.sweet > 1)
        ctx.addIssue({
            code: 'custom',
            path: ['vio', 'sweet'],
            message: 'The lawyer and the sweet spot must fit the bar.',
        });
    if (h.smokeAt > h.dentsMax)
        ctx.addIssue({ code: 'custom', path: ['smokeAt'], message: 'Within the dents a keke takes.' });
});
/**
 * Starting values (fixed game numbers, reviewed monthly, per the default the plan records): a
 * 10-minute shift, a quarter tank and ₦2,000 float. Balanced on the beta Saturdays (CW-38).
 */
export const DEFAULT_HUSTLE = {
    shiftMs: 600_000,
    countdownMs: 3_000,
    float: 2_000,
    tankLitres: 8,
    startLitres: 2,
    litresPerTile: 0.004,
    speed: 1,
    expresswaySpeed: 1.35,
    fuelPrice: 1_000,
    queueBusyMs: 8_000,
    queueQuietMs: 1_500,
    fillMsPerLitre: 400,
    fareBase: 500,
    farePerTile: 25,
    riderFare: { student: 0.7, office: 1.4, market: 1.3, night: 1.6 },
    rushFactor: 1.5,
    rainFactor: 1.3,
    wrongDropShare: 0.5,
    tipShare: 0.15,
    loadMs: { student: 800, office: 800, market: 3_000, night: 800 },
    officePatienceHours: 2,
    waitHours: 1.5,
    spawnEveryMs: 2_500,
    busyScale: 1,
    seats: 3,
    reach: 1.5,
    parkLevy: 200,
    dayTicket: 1_000,
    settle: 300,
    latchSlow: 0.5,
    latchCut: 0.25,
    latchForceMs: 30_000,
    seizeHoldMs: 6_000,
    bankKeep: 1_000,
    roger: 500,
    policeWaitMs: 8_000,
    policeChoiceMs: 8_000,
    policeReach: 1.5,
    policeCoolMs: 90_000,
    vio: { sightTiles: 5, needleMs: 4_000, lawyer: 0.15, sweet: 0.45, coolMs: 15_000, vanTilesPerSec: 3 },
    bribeShare: 0.5,
    dentSlow: 0.06,
    dentsMax: 5,
    wallCrash: 0.8,
    smokeAt: 4,
    dentFix: 1_500,
    potholeSpeed: 0.7,
    punctureSlow: 0.5,
    puncture: 1_000,
    pushSpeed: 0.25,
    goslowSlow: 0.4,
    ownerDelivery: 5_000,
};
/**
 * A Saturday's city events (the spec's Saturday events), drawn from the Saturday's seed so every
 * player that day plays the same city and the hub can announce them: one or two of a fuel scarcity
 * (half the stations shut), a market day at Mile 12, a VIO operation in one district and a bridge
 * go-slow. Rain is the Saturday's weather, not drawn here.
 */
export const HUSTLE_EVENTS = ['scarcity', 'market', 'operation', 'goslow'];
/** The districts a VIO operation may work (every one with a van's beat; Ojota is home and has the op van only). */
export const OPERATION_DISTRICTS = ['oshodi', 'yaba', 'ikeja', 'surulere', 'mile12', 'ojota'];
/** A 32-bit mix of the seed and a salt (no generator: the hub and the simulation agree from the seed alone). */
const mix = (seed, salt) => {
    let h = Math.imul((seed ^ salt) >>> 0, 0x9e3779b1) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
    return (h ^ (h >>> 16)) >>> 0;
};
/** The Saturday's events from its seed: one, or two a third of the time, never the same twice. */
export function hustleDay(seed) {
    const first = mix(seed, 0x45564e31) % HUSTLE_EVENTS.length;
    const events = [HUSTLE_EVENTS[first]];
    if (mix(seed, 0x45564e32) % 3 === 0) {
        const second = (first + 1 + (mix(seed, 0x45564e33) % (HUSTLE_EVENTS.length - 1))) % HUSTLE_EVENTS.length;
        events.push(HUSTLE_EVENTS[second]);
    }
    return events.includes('operation')
        ? {
            events,
            operation: OPERATION_DISTRICTS[mix(seed, 0x4f505231) % OPERATION_DISTRICTS.length],
        }
        : { events };
}
//# sourceMappingURL=compete.js.map